import {
  Button,
  buttonVariants,
  InfoButton,
  Modal,
  Para,
  RadioOption,
  RadioOptions,
  Stack,
} from 'ui-components';
import { useMemo, useState } from 'react';
import { ScheduleSelect } from './ScheduleSelect';
import { DateTime } from 'luxon';
import { useCheckoutStore } from '../checkoutStore';
import { CheckoutEnum } from 'drycut-graphql';
import { twJoin } from 'tailwind-merge';

type Props = {
  disabled?: boolean;
};

export function ScheduledRadioOptions({ disabled = false }: Props): React.ReactElement {
  const [scheduleOpen, setScheduleOpen] = useState<boolean>(false);
  const selectedSchedule = useCheckoutStore((state) => state.selectedSchedule);
  const scheduled = useCheckoutStore((state) => state.scheduled);
  const setSelectedSchedule = useCheckoutStore((state) => state.setSelectedSchedule);
  const toggleScheduled = useCheckoutStore((state) => state.toggleScheduled);
  const selectedStore = useCheckoutStore((state) => state.selectedStore);

  const { wrapper, text } = buttonVariants({
    fill: 'ghost',
    size: 'small',
  });

  const preorderDescription = useMemo(() => {
    if (selectedSchedule) {
      return (
        <Stack direction="col" gap={1} className="items-start">
          <Para size="sm">Deine Buching für</Para>
          <Stack direction="row" gap={1}>
            <Para as="span" weight="bold" size="sm">
              {selectedSchedule.setLocale('de-CH').toFormat('EEE HH:mm')}
            </Para>
            <span>&ndash;</span>
            <Para size="sm">{selectedSchedule.plus({ minutes: 15 }).toFormat('HH:mm')}</Para>
          </Stack>

          <Button
            size="small"
            onClick={() => setScheduleOpen(true)}
            className={wrapper({
              className: 'p-1 hover:bg-primary-300/10 active:bg-primary-300/15',
            })}
          >
            <Para
              align="left"
              className={text({
                className:
                  'underline underline-offset-2 group-data-[checked]:text-primary-600 text-left',
              })}
            >
              Ändern
            </Para>
          </Button>
        </Stack>
      );
    } else {
      return (
        <Para size="sm" pretty>
          Wähle einen <u>späteren</u> Termin für Dich aus
        </Para>
      );
    }
  }, [selectedSchedule, wrapper, text]);

  const options: RadioOption[] = useMemo(
    () => [
      {
        name: 'Sofort schneiden',
        description: (
          <Para size="sm" pretty>
            Sichere Dir den <u>nächsten</u> verfügbaren Cut
          </Para>
        ),
        disabled:
          selectedStore?.checkoutType === CheckoutEnum.Scheduled ||
          !selectedStore ||
          !selectedStore.slotsAvailable,
      },
      {
        name: 'Später schneiden',
        description: preorderDescription,
        disabled: selectedStore?.checkoutType === CheckoutEnum.Instant || !selectedStore,
      },
    ],
    [selectedStore, preorderDescription],
  );

  const selectedOption = useMemo(() => {
    if (!selectedStore?.slotsAvailable && !scheduled) {
      return;
    }
    return scheduled ? options[1] : options[0];
  }, [scheduled, selectedStore, options]);

  const handleScheduleClose = () => {
    if (!selectedSchedule) {
      toggleScheduled();
    }
    setScheduleOpen(false);
  };

  const handleSlotSelect = (date: DateTime) => {
    setSelectedSchedule(date);
    setScheduleOpen(false);
  };

  const handleRadioChange = (option: RadioOption) => {
    toggleScheduled();

    if (option.name === options[1].name) {
      setScheduleOpen(true);
    } else {
      setSelectedSchedule();
    }
  };

  return (
    <>
      <div className={twJoin('relative', disabled && 'opacity-40')}>
        <RadioOptions
          options={options}
          selected={selectedOption}
          onChange={handleRadioChange}
          disabled={disabled}
        />

        {/* NOT NEW ANYMORE */}
        {/* <Stack className="absolute z-10 -bottom-4 -right-4 bg-red-600 text-white tracking-wider pointer-events-none rounded-full size-14 items-center justify-center rotate-12"> */}
        {/*   <Para className="font-bold text-xl">Neu</Para> */}
        {/* </Stack> */}
      </div>

      <Modal
        title="Wähle Dein Zeitfenster aus"
        open={scheduleOpen}
        closeButton
        onClose={handleScheduleClose}
        className="max-w-prose w-full sm:w-auto"
      >
        <Para size="sm" pretty className="hidden sm:flex">
          Die genaue Zeit hängt von vorangegangenen Terminen ab. Daher geben wir Dir nicht eine
          spezifische Zeit, sondern ein Zeitfenster vor, in welchem der Schnitt voraussichtlich
          anfängt. Komm bitte rechtzeitig (5 Min vorher) in den Salon, damit wir Dich zügig bedienen
          können.
        </Para>

        <Stack direction="row" className="justify-center flex sm:hidden" gap={1}>
          <Para size="xs" pretty>
            Wie unsere Vorausbuchung funktioniert
          </Para>

          <InfoButton
            title="Wie unsere Vorausbuchung funktioniert"
            text="Die genaue Zeit hängt von vorangegangenen Terminen ab. Daher geben wir Dir nicht eine
          spezifische Zeit, sondern ein Zeitfenster vor, in welchem der Schnitt voraussichtlich
          anfängt. Komm bitte rechtzeitig (5 Min vorher) in den Salon, damit wir Dich zügig bedienen
          können."
          />
        </Stack>
        <ScheduleSelect onSlotSelect={handleSlotSelect} />
      </Modal>
    </>
  );
}
